/**
 * useMarketInsights
 *
 * Fetches and organises all market insight and monthly report documents
 * from the CMS for use on the Library page.
 *
 * Legacy static data has been fully replaced by CMS calls.
 *
 * Usage:
 *   const {
 *     latestWeekly, weeklyHistory,
 *     latestMonthly, monthlyHistory,
 *     loading, error
 *   } = useMarketInsights();
 *
 * Returns:
 * {
 *   loading: boolean,
 *   error: string | null,
 *
 *   latestWeekly:  { name, url, year, week, label } | null,
 *   weeklyHistory: [...],    // all weekly insights sorted newest first
 *
 *   latestMonthly:  { name, url, year, month, label } | null,
 *   monthlyHistory: [...],   // all monthly reports sorted newest first
 * }
 */

import { useState, useEffect } from 'react';

import {
  getWeeklyInsights,
  getMonthlyReports,
  getWeeklyInsightContent
} from '../services/cmsService';

const useMarketInsights = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestWeekly, setLatestWeekly] = useState(null);
  const [weeklyHistory, setWeeklyHistory] = useState([]);
  const [latestMonthly, setLatestMonthly] = useState(null);
  const [monthlyHistory, setMonthlyHistory] = useState([]);
  const [weeklyInsightContent, setWeeklyInsightContent] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const [weekly, monthly, insightContent] = await Promise.all([
          getWeeklyInsights(),
          getMonthlyReports(),
          getWeeklyInsightContent()
        ]);

        if (cancelled) return;

        setWeeklyHistory(weekly);
        setLatestWeekly(weekly[0] ?? null);

        setMonthlyHistory(monthly);
        setLatestMonthly(monthly[0] ?? null);

        setWeeklyInsightContent(insightContent);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load market insights');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    loading,
    error,
    latestWeekly,
    weeklyHistory,
    latestMonthly,
    monthlyHistory,
    weeklyInsightContent
  };
};

export default useMarketInsights;
